import React, { useState } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Modal, Button } from "react-bootstrap";
import { Input } from "@bootstrap-styled/v4";
import { colors } from "utils";
import { CloseButton } from "components/shared/CloseButton/CloseButton";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";

export const ReportModal = ({
  show,
  onHide,
  defaultMessage = "",
  contextMessage = "",
  reportLocation = "Educator Dashboard",
  extraPayload = {},
}) => {
  const [reportText, setReportText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    if (isLoading) return;
    onHide();
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const prefix = contextMessage || defaultMessage;
    const details = reportText
      ? `${prefix ? prefix + "\n\nAdditional info: " : ""}${reportText}`
      : prefix;

    await request("feedback/submit_custom_feedback", {
      payload: {
        feedback_type: "bug",
        details,
        report_location: reportLocation,
        ...extraPayload,
      },
    })
      .then(() => {
        onHide();
        setReportText("");
      })
      .catch((e) => {
        console.error(e);
        logError("Failed to submit feedback report", e, {
          category: LogCategory.ERROR,
        });
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <ReportModalContainer show={show} onHide={handleClose}>
      {isLoading && (
        <ReportModalSpinner>
          <FontAwesomeIcon icon={faSpinner} spin size="5x" />
        </ReportModalSpinner>
      )}
      <ReportModalHeader>
        <CloseButton
          onClick={handleClose}
          color="#000"
          width={24}
          height={24}
          customCss="position: initial;"
        />
        <span style={{ fontSize: "20px", fontWeight: 700, textAlign: "center", width: "100%" }}>
          Report an Issue
        </span>
      </ReportModalHeader>
      <ReportModalBody>
        {defaultMessage && (
          <ReportModalDefaultText>{defaultMessage}</ReportModalDefaultText>
        )}
        <Input
          placeholder="Add any additional details here (optional)..."
          maxLength={500}
          type="textarea"
          style={{ height: "100px", padding: "0.75rem 1rem", minHeight: "150px", fontWeight: 300, fontSize: "0.875rem" }}
          onChange={(e) => setReportText(e.target.value)}
          value={reportText}
        />
      </ReportModalBody>
      <ReportModalFooter>
        <Button
          onClick={handleSubmit}
          style={{
            backgroundColor: "#605DEC",
            border: "none",
            fontSize: "0.875rem",
            padding: "0.5rem 1rem",
            cursor: isLoading ? "not-allowed" : "pointer",
            opacity: isLoading ? 0.5 : 1,
            margin: 0,
          }}
          disabled={isLoading}
        >
          Submit Report
        </Button>
      </ReportModalFooter>
    </ReportModalContainer>
  );
};

const ReportModalContainer = styled(Modal)`
  width: 100%;

  > div {
    max-width: 388px;

    .modal-content {
      border-radius: 1rem;
    }
  }
`;

const ReportModalSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
`;

const ReportModalHeader = styled(Modal.Header)`
  display: flex;
  flex-direction: column;
  padding: 1rem 2rem 0;
  gap: 1rem;
  align-items: flex-end;
  justify-content: flex-end;
  height: 100px;
  border: none;
  margin: 0;
`;

const ReportModalBody = styled(Modal.Body)`
  padding: 1rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ReportModalDefaultText = styled.p`
  font-size: 0.875rem;
  color: ${colors.nightlyWoods700};
  margin: 0;
  text-align: center;
  font-style: italic;
`;

const ReportModalFooter = styled(Modal.Footer)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  padding-top: 0;
  padding-bottom: 1rem;
`;
